import { type ReactNode, useRef, useState } from 'react';
import { type Dictionary, useLocale, useText } from '../i18n/LocaleProvider';
import { ActionRecoveryError } from './ActionRecoveryError';

// Shared UI building blocks. Server answers are shown as received; error
// reasons map to fixed bilingual messages, never raw server text.

export function Alert({
  kind,
  children,
}: {
  readonly kind: 'error' | 'success' | 'info';
  readonly children: ReactNode;
}) {
  return (
    <p className={`er-alert ${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      {children}
    </p>
  );
}

export function Field({
  id,
  label,
  hint,
  children,
}: {
  readonly id: string;
  readonly label: string;
  readonly hint?: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="er-field">
      <label htmlFor={id}>{label}</label>
      {children}
      {hint && <span className="er-hint">{hint}</span>}
    </div>
  );
}

// A fresh idempotency key per user action; retries of the SAME action reuse
// it (X-02), a new action gets a new one.
export function newRequestKey(): string {
  return `web-${crypto.randomUUID()}`;
}

const errors: Dictionary<
  | 'forbidden'
  | 'notFound'
  | 'conflict'
  | 'invalid'
  | 'precondition'
  | 'unavailable'
  | 'unauthenticated'
  | 'tooMany'
  | 'unexpected'
> = {
  en: {
    forbidden: 'You are not allowed to do this.',
    notFound: 'It was not found, or it is outside your scope.',
    conflict: 'This conflicts with the current state. Refresh and try again.',
    invalid: 'Some values are not valid. Check the highlighted fields.',
    precondition: 'This is not possible in the current state.',
    unavailable: 'The service is temporarily unavailable. Please try again.',
    unauthenticated: 'Your session has ended. Please sign in again.',
    tooMany: 'Too many attempts. Please wait and try again.',
    unexpected: 'Something went wrong.',
  },
  ar: {
    forbidden: 'غير مسموح لك بتنفيذ هذا الإجراء.',
    notFound: 'غير موجود، أو خارج نطاق صلاحياتك.',
    conflict: 'يتعارض هذا مع الحالة الحالية. حدّث الصفحة وحاول مرة أخرى.',
    invalid: 'بعض القيم غير صالحة. راجع الحقول المحددة.',
    precondition: 'هذا الإجراء غير ممكن في الحالة الحالية.',
    unavailable: 'الخدمة غير متاحة مؤقتًا. يرجى المحاولة مرة أخرى.',
    unauthenticated: 'انتهت جلستك. يرجى تسجيل الدخول مرة أخرى.',
    tooMany: 'محاولات كثيرة. يرجى الانتظار ثم المحاولة.',
    unexpected: 'حدث خطأ ما.',
  },
};

export interface Failure {
  readonly status: number;
  readonly reason?: string;
}

export function failureOf(response: { readonly status: number; readonly body: unknown }): Failure | null {
  if (response.status < 400) return null;
  const reason = (response.body as { error?: { reason?: string } } | null)?.error?.reason;
  return { status: response.status, ...(reason ? { reason } : {}) };
}

class ReadFailure extends Error {
  constructor(readonly failure: Failure) {
    super('read_failed');
  }
}

// Keep refused/failed reads distinct from successful empty projections. Only
// fixed localized status/reason mappings reach the UI, never server text.
export function readResponse<R extends { readonly status: number; readonly body: unknown }>(
  response: R,
): Extract<R, { readonly status: 200 }>['body'] {
  if (response.status !== 200) throw new ReadFailure(failureOf(response) ?? { status: 500 });
  return response.body as Extract<R, { readonly status: 200 }>['body'];
}

export function QueryFailure({
  query,
}: {
  readonly query: {
    readonly isError: boolean;
    readonly error: unknown;
    readonly isFetching: boolean;
    readonly refetch: () => unknown;
  };
}) {
  const message = useFailureMessage();
  const { locale } = useLocale();
  if (!query.isError) return null;
  const failure = query.error instanceof ReadFailure ? query.error.failure : { status: 503 };
  return (
    <>
      <Alert kind="error">{message(failure)}</Alert>
      <button
        className="er-button secondary"
        type="button"
        disabled={query.isFetching}
        onClick={() => {
          void query.refetch();
        }}
      >
        {locale === 'ar' ? 'إعادة المحاولة' : 'Try again'}
      </button>
    </>
  );
}

// Specific reasons may be given their own message by the caller.
export function useFailureMessage(): (failure: Failure, specific?: Readonly<Record<string, string>>) => string {
  const t = useText(errors);
  return (failure, specific) => {
    if (failure.reason && specific?.[failure.reason]) return specific[failure.reason] as string;
    switch (failure.status) {
      case 400:
        return t.invalid;
      case 401:
        return t.unauthenticated;
      case 403:
        return t.forbidden;
      case 404:
        return t.notFound;
      case 409:
        return t.conflict;
      case 422:
        return t.precondition;
      case 429:
        return t.tooMany;
      case 503:
        return t.unavailable;
      default:
        return t.unexpected;
    }
  };
}

type ActionResponse = { readonly status: number; readonly body: unknown };

// An uncertain outcome retains BOTH the original callback (its captured request)
// and key. Any next submission reconciles that original action first. A new
// command starts only after a success or a definite rejection. In-flight calls
// share one promise, including double clicks before React renders busy state.
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const message = useFailureMessage();
  const { locale } = useLocale();
  const pending = useRef<{
    key: string;
    call: (key: string) => Promise<ActionResponse>;
    specific: Readonly<Record<string, string>> | undefined;
    onFailure: ((failure: Failure) => void) | undefined;
    uncertain: boolean;
  } | null>(null);
  const inFlight = useRef<Promise<ActionResponse | null> | null>(null);
  const retryNotice =
    locale === 'ar'
      ? 'لم تتأكد نتيجة العملية. أعد المحاولة للتحقق من العملية الأصلية بنفس البيانات قبل بدء عملية أخرى.'
      : 'The outcome is uncertain. Retry to resolve the original action with its original data before starting another action.';
  return {
    busy,
    error,
    setError,
    async run<R extends { readonly status: number; readonly body: unknown }>(
      call: (requestKey: string) => Promise<R>,
      specific?: Readonly<Record<string, string>>,
      options?: { readonly onFailure?: (failure: Failure) => void },
    ): Promise<R | null> {
      if (inFlight.current) return inFlight.current as Promise<R | null>;
      const attempt = pending.current ?? {
        key: newRequestKey(),
        call,
        specific,
        onFailure: options?.onFailure,
        uncertain: false,
      };
      pending.current = attempt;
      setBusy(true);
      setError(null);
      const work = (async () => {
        // Publish the shared promise before even a synchronous callback error
        // can finish the attempt and clear it.
        await Promise.resolve();
        try {
          const response = await attempt.call(attempt.key);
          const failure = failureOf(response);
          if (failure) {
            // A later authorization/session denial cannot prove whether the
            // earlier unknown attempt committed. Keep its identity until a
            // successful replay resolves it under current authorization.
            attempt.uncertain ||= failure.status >= 500 || failure.status === 408;
            if (!attempt.uncertain) pending.current = null;
            setError(`${message(failure, attempt.specific)}${attempt.uncertain ? ` ${retryNotice}` : ''}`);
            attempt.onFailure?.(failure);
            return null;
          }
          pending.current = null;
          return response;
        } catch (cause) {
          if (cause instanceof ActionRecoveryError) {
            pending.current = null;
            if (cause.status) {
              setError(`${message({ status: cause.status })} ${retryNotice}`);
              return null;
            }
            setError(
              locale === 'ar'
                ? 'توجد عملية مالية غير محسومة أو تعذر حفظ هوية العملية. أدخل بيانات العملية الأصلية كما هي وأعد المحاولة في علامة التبويب نفسها. لا تمسح بيانات المتصفح؛ اطلب المساعدة إذا تعذر الاسترداد.'
                : 'A financial action needs recovery, or its identity could not be saved. Re-enter the original data exactly and retry in the same tab. Keep browser data; seek assistance if recovery is unavailable.',
            );
            return null;
          }
          attempt.uncertain = true;
          pending.current = attempt;
          setError(`${message({ status: 503 })} ${retryNotice}`);
          return null;
        } finally {
          inFlight.current = null;
          setBusy(false);
        }
      })();
      inFlight.current = work;
      return work as Promise<R | null>;
    },
  };
}

// Reads have no financial intent to retain. A new search uses current input;
// only the latest submitted search may publish results or failure state.
export function useReadAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);
  const message = useFailureMessage();
  return {
    busy,
    error,
    // Supersede any in-flight read without starting one, e.g. when another
    // search publishes into the same results (SA-04): its outcome is dropped.
    cancel() {
      generation.current++;
      setBusy(false);
      setError(null);
    },
    async run<R extends ActionResponse>(call: () => Promise<R>): Promise<R | null> {
      const current = ++generation.current;
      setBusy(true);
      setError(null);
      try {
        const response = await call();
        if (current !== generation.current) return null;
        const failure = failureOf(response);
        if (failure) {
          setError(message(failure));
          return null;
        }
        return response;
      } catch {
        if (current === generation.current) setError(message({ status: 503 }));
        return null;
      } finally {
        if (current === generation.current) setBusy(false);
      }
    },
  };
}

export function Loading() {
  const { locale } = useLocale();
  return <p role="status">{locale === 'ar' ? 'جارٍ التحميل…' : 'Loading…'}</p>;
}

export function Badge({
  tone,
  children,
}: {
  readonly tone: 'ok' | 'off' | 'warn' | 'danger';
  readonly children: ReactNode;
}) {
  return <span className={`er-badge ${tone}`}>{children}</span>;
}

// Role names (PD-10.02). Arabic renderings are UI wording for HAA review.
export const ROLE_LABELS: Readonly<Record<string, Readonly<Record<'ar' | 'en', string>>>> = {
  ceo: { en: 'CEO', ar: 'الرئيس التنفيذي' },
  manager: { en: 'Manager', ar: 'المدير' },
  accountant: { en: 'Accountant', ar: 'المحاسب' },
  booking_responsible: { en: 'Booking Responsible', ar: 'مسؤول الحجوزات' },
  operations: { en: 'Operations', ar: 'العمليات' },
};
